<!-- eslint-disable vue/no-template-shadow -->

<script lang="ts">
import formatPrettyNumber from '@/modules/common/components/pretty-number/formatPrettyNumber';
import { MAX_RATE, RATE_PRECISION } from '@/modules/common/constants/precision';
import { notGreaterThanPrecision } from '@/utils/helpers/custom-validators';
import { computed, nextTick, onBeforeMount, shallowRef, watch, watchEffect } from 'vue';
import { Api } from '@/modules/common/types/api';
import { RawSecurity, Security, TimeTuple } from '@/modules/common/models';
import { SecurityCategory } from '@/connect/gen/consts/instrumentconsts_pb';
import { DialogFormStatus } from '@/modules/common/types/dialog';
import {
  orderIntentOptions,
  rateTypeOptions,
  timeInForceOptionsForOrder,
} from '@/modules/marketplace/helpers/marketplace';
import {
  OrderIntent,
  OrderSide,
  OrderType,
  TimeInForceType,
} from '@/modules/marketplace/types/marketplace';
import { ApiError } from '@/utils/errors';
import { calculateOrderNotional, formatDecimalAsString } from '@/utils/helpers/auction-numbers';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { RiskLimitValidator } from '@/utils/helpers/risk-limit-validator';
import Decimal from 'decimal.js';
import {
  CreateOmsOrderRequest,
  ModifyOmsOrderRequest,
  OmsOrder,
} from '@/modules/marketplace/models';
import { AgreementInfo } from '@/modules/agreements/models';
import { Data, OmsOrderType, RateType, Side, Timestamp } from '@/connect';
import { BookOrder } from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import { useAgreementsStore } from '@/store/agreements-store';
import { useSecuritiesStore } from '@/store/securities-store';
import PlainTimePicker from '@/modules/common/components/PlainTimePicker.vue';
import { addHours, setHours, setMinutes, setSeconds, subMinutes } from 'date-fns';
import { isTimeInTimeframe } from '@/modules/market-closed/helpers/market-closed';
import { useRate } from '@/modules/marketdata/composables/benchmarks';
import { useRateTypeStore } from '@/store/rate-type-store';
import { store as vuex } from '@/store/store';
import { useRules } from '@/composables/vuetify-validation';
import { useCurrentTime } from '@/composables/current-time';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';
import { useDialogManager } from '@/plugins/dialog-manager';
import { SelectValues } from '@/types/select';
import { marketplaceService } from '@/connect/services/marketplace';
import { FrontendOrder } from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { OmsTimeInForceType } from '@/connect/gen/consts/omsconsts_pb';
import i18n from '@/localisation/i18n';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { ifTimestampToDate, utcToLocal } from '@/utils/helpers/dates';
import MarketplaceOrderFormMyOrderPicker, {
  OrderSelection,
} from './MarketplaceOrderFormMyOrderPicker.vue';
import { useEventListener } from '@/composables/native';
import SimpleSecuritySearch from '@/modules/manual-loan/components/SimpleSecuritySearch.vue';
import MarketplaceOrderSummary from '@/modules/marketplace/components/MarketplaceOrderSummary.vue';
import MultipleTableAgreementSelector from '@/modules/agreements/components/MultipleTableAgreementSelector.vue';

export type PopulateFormPayload = Pick<
  OmsOrder,
  | 'side'
  | 'quantity'
  | 'minQuantity'
  | 'filled'
  | 'security'
  | 'fee'
  | 'rebate'
  | 'orderType'
  | 'orderIntent'
  | 'timeInForceType'
  | 'goodTillTime'
  | 'agreements'
  | 'isAnonymous'
  | 'allowOddLots'
>;

const EQUITY_MIN_QUANTITY = 100;
const BOND_MIN_QUANTITY = 1000;

function goodTillTimeToDate(now: Date, value: TimeTuple): Date {
  return setSeconds(setMinutes(setHours(now, value[0]), value[1]), 0);
}
</script>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/unified-signatures */

const dialogManager = useDialogManager();
const snackbarManager = useSnackbarManager();
const marketplaceApiService = useMarketplaceApiService();

const securitiesStore = useSecuritiesStore();
const agreementsStore = useAgreementsStore();

const props = withDefaults(
  defineProps<{
    orderRef?: string | null;
    orderToMatch?: BookOrder | null;
    newOrderSide?: OrderSide | null;
    newOrderSecurity?: Security | null;
    showSummary?: boolean;
    shouldSplit?: boolean;
    value?: boolean;
  }>(),
  {
    orderRef: null,
    orderToMatch: null,
    newOrderSide: null,
    newOrderSecurity: null,
    shouldSplit: false,
    showSummary: false,
    value: true,
  }
);

const emit = defineEmits<{
  (event: 'update:show-summary', value: boolean): void;
  (event: 'action', value: { orderRef: string; message: string });
  (event: 'success'): void;
  (event: 'input', value: boolean): void;
  (event: 'reset'): void;
}>();

const order = shallowRef<OmsOrder | null>(null);

const rateTypeStore = useRateTypeStore();
const { fee, rebate } = useRate();

// rate + fee = benchmark (const), we need to make sure that rate is still same,
// but only if we're not editing the existing order, otherwise we want it to be
// in sync with what's displayed in the Explorer table
watch(
  () => rateTypeStore.marketplaceRateType,
  () => {
    if (order.value === null && props.orderToMatch === null) {
      [fee.value, rebate.value] = [rebate.value, fee.value];
    }
  }
);

const rate = computed({
  get() {
    return rateTypeStore.marketplaceRateType === RateType.FEE ? fee.value : rebate.value;
  },
  set(value) {
    if (rateTypeStore.marketplaceRateType === RateType.FEE) {
      fee.value = value;
    } else {
      rebate.value = value;
    }
  },
});

const isEditMode = computed(() => !!props.orderRef);
const formTitle = computed(() =>
  typeof myOrderSelected.value === 'object' ? 'Match from' : isEditMode.value ? 'Edit' : 'Create'
);
const loginState = computed(() => vuex.state.loginState!);
const clientConfig = computed(() => vuex.state.clientConfig!);
const { currentTimeUTC, localTimezone } = useCurrentTime();
const canBorrow = computed(() => vuex.getters.canBorrow);
const canLend = computed(() => vuex.getters.canLend);

const quantity = shallowRef<null | number>(null);
const minQuantity = shallowRef<null | number>(EQUITY_MIN_QUANTITY);
const minQuantityTouched = shallowRef(false);
const remainingQuantity = computed(() =>
  quantity.value == null ? 1 : quantity.value - filled.value
);
const filled = shallowRef(0);
const orderType = shallowRef<OrderType>('LIMIT');
const orderIntent = shallowRef<OrderIntent>('FIRM');

const goodTillTime = shallowRef<TimeTuple | null>(null);
const goodTillTimeDate = computed(() =>
  goodTillTime.value === null ? null : goodTillTimeToDate(currentTimeUTC.value, goodTillTime.value)
);
const timeInForceType = shallowRef<TimeInForceType>('DAY');
watchEffect(() => {
  if (timeInForceType.value === 'GOOD_TILL_TIME' && goodTillTime.value === null) {
    const dt = utcToLocal(nextHour.value);
    goodTillTime.value = dt === null ? null : [dt.getHours(), dt.getMinutes()];
  }
});

const agreements = shallowRef<AgreementInfo[]>([]);
const agreementIdsEligible = shallowRef(new Set<string>());
const isAnonymous = shallowRef(false);
const allowOddLots = shallowRef(true);
const agreementSelectorKey = shallowRef(0);
const expandedPanel = shallowRef<number | null>(null);
const apiErrors = shallowRef<string[]>([]);

const sideSelectorItems = computed(() => {
  const items: SelectValues<OrderSide> = [];
  if (canBorrow.value) items.push({ value: 'BORROW', text: 'Borrower' });
  if (canLend.value) items.push({ value: 'LOAN', text: 'Lender' });
  return items;
});

function gttTupleToDate(time: TimeTuple | null): Date {
  const [hours, minutes] = time ?? [12, 0];
  return setSeconds(setMinutes(setHours(currentTimeUTC.value, hours), minutes), 0);
}

const formFields = computed(() => {
  let formGoodTillTime: Date | null = null;
  if (timeInForceType.value === 'GOOD_TILL_TIME') {
    formGoodTillTime = gttTupleToDate(goodTillTime.value);
  }

  return {
    side: side.value,
    quantity: quantity.value as number,
    minQuantity: minQuantity.value === null ? defaultMinQuantity.value : minQuantity.value,
    filled: filled.value,
    security: security.value as Security,
    fee: fee.value as Decimal,
    rebate: rebate.value as Decimal,
    orderType: orderType.value,
    orderIntent: orderIntent.value,
    timeInForceType: timeInForceType.value,
    goodTillTime: formGoodTillTime,
    agreements: agreements.value,
    isAnonymous: isAnonymous.value,
    allowOddLots: allowOddLots.value,
  };
});

const isMarketOrder = computed(() => orderType.value === 'MARKET');
const nextHour = computed(() => setMinutes(addHours(currentTimeUTC.value, 1), 0));
const timeInForceOptions = computed(() =>
  timeInForceOptionsForOrder(orderType.value, orderIntent.value)
);

watch(
  () => props.value,
  (isOpen, wasOpen) => {
    if (!isOpen || wasOpen) {
      // drawer is already open or was just closed
      return;
    }

    // drawer has just opened, we must init the form
    if (props.orderRef) {
      void fetchOrder(props.orderRef);
    } else if (props.orderToMatch) {
      populateFromBookOrder();
    } else {
      // opening in create mode
      resetFields();
    }
  }
);

const navDrawerPermanent = shallowRef(false);

const localShowSummary = shallowRef(false);
watch(localShowSummary, (value) => {
  emit('update:show-summary', value);
  if (!value) {
    resetValidation();
    apiErrors.value = [];
  }
});

watchEffect(() => (localShowSummary.value = props.showSummary));

const side = shallowRef<OrderSide>('BORROW');
watchEffect(() => {
  if (props.newOrderSide !== null) side.value = props.newOrderSide;
});

const security = shallowRef<Security | null>(null);
watchEffect(() => {
  if (props.newOrderSecurity !== null) security.value = props.newOrderSecurity;
});

const defaultMinQuantity = computed(() => {
  const category = security.value?.securityCategory;
  const isFixedIncome =
    category === SecurityCategory.FIXED_INCOME || category === ('FIXED_INCOME' as unknown);
  return isFixedIncome ? BOND_MIN_QUANTITY : EQUITY_MIN_QUANTITY;
});

// When security changes (e.g. user enters a bond ticker), update minQuantity to match the new default
watch(defaultMinQuantity, (newDefault) => {
  if (!minQuantityTouched.value) {
    minQuantity.value = quantity.value ? Math.min(quantity.value, newDefault) : newDefault;
  }
});

watch(minQuantity, (value) => {
  if (!quantity.value || !value) return;

  // the moment the minQuantity is changed to something that isn't the quantity or the default
  // it must be that the user manually touched the field
  if (value !== quantity.value && value !== defaultMinQuantity.value) {
    minQuantityTouched.value = true;
  }
});

watch(
  quantity,
  (value) => {
    if (!value) return;

    // as long as the min quantity has not been touched by the user we make it follow the quantity (up to the default)
    if (!minQuantityTouched.value) {
      minQuantity.value = Math.min(value, defaultMinQuantity.value);
    }
  },
  { flush: 'sync' }
);

// @TODO: not currently used, but could be useful when type "MARKET" is supported
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function onOrderTypeChanged(): void {
  if (orderType.value === 'MARKET') {
    fee.value = null;
    timeInForceType.value = 'IMMEDIATE_OR_CANCEL';
  } else {
    timeInForceType.value = 'DAY';
  }
}

onBeforeMount(() => {
  resetFields();

  if (props.orderToMatch) {
    populateFromBookOrder();
    return;
  }

  if (props.orderRef) {
    // Pull data from current order (edit) to be used inside the form
    void fetchOrder(props.orderRef);
    return;
  }
});

let abortController: AbortController | null = null;
const formStatus = shallowRef<DialogFormStatus>('idle');
async function fetchOrder(orderRef: string): Promise<void> {
  if (abortController) abortController.abort();
  abortController = new AbortController();

  try {
    const response = await marketplaceApiService.fetchOrderDetails(
      orderRef,
      abortController.signal
    );

    if (!response) return;

    order.value = response.order;
    initializeFormFromOrder();
    // start fresh so "changed" validation works correctly
    nextTick(resetValidation);
  } catch (err) {
    apiErrors.value = [new ApiError(i18nServerMessage(err as Error)).message];
    formStatus.value = 'idle';
    order.value = null;
  }
}

function initializeFormFromOrder(): void {
  if (!order.value) return;

  const agreements = order.value.agreements.map(
    (partialAgreement) => agreementsStore.getAgreement(partialAgreement.id) ?? partialAgreement
  );

  populateForm({
    ...order.value,
    agreements,
  });

  if (minQuantity.value && minQuantity.value > defaultMinQuantity.value) {
    minQuantityTouched.value = true;
  } else {
    minQuantityTouched.value = quantity.value !== minQuantity.value;
  }
}

function populateForm(payload: PopulateFormPayload): void {
  const dt = payload.goodTillTime ?? nextHour.value;
  const newGoodTillTime: TimeTuple | null = dt === null ? null : [dt.getHours(), dt.getMinutes()];

  side.value = payload.side;
  quantity.value = payload.quantity;
  minQuantity.value = payload.minQuantity;
  filled.value = payload.filled;
  security.value = payload.security;
  // Set the displayed rate last so syncRates preserves it
  // (the "last updated" ref is protected from being overwritten by the sync mechanism)
  if (rateTypeStore.marketplaceRateType === RateType.FEE) {
    rebate.value = payload.rebate;
    fee.value = payload.fee;
  } else {
    fee.value = payload.fee;
    rebate.value = payload.rebate;
  }
  orderType.value = payload.orderType;
  orderIntent.value = payload.orderIntent;
  timeInForceType.value = payload.timeInForceType;
  goodTillTime.value = newGoodTillTime;
  agreements.value = payload.agreements;
  isAnonymous.value = payload.isAnonymous;
  allowOddLots.value = payload.allowOddLots;
}

function populateFromBookOrder(): void {
  const orderToMatch = props.orderToMatch as BookOrder;
  const agreements = orderToMatch.agreementIds.reduce<AgreementInfo[]>((acc, agreementId) => {
    const agreement = agreementsStore.getAgreement(agreementId);
    if (!agreement) return acc;
    acc.push(AgreementInfo.fromData(agreement));
    return acc;
  }, []);

  const payload: PopulateFormPayload = {
    side: orderToMatch.side === Side.BORROW ? 'LOAN' : 'BORROW',
    quantity: Number(orderToMatch.quantity),
    minQuantity: Math.min(Number(orderToMatch.minExecutionQuantity), defaultMinQuantity.value),
    filled: 0,
    security: Security.fromData(securitiesStore.getSecurity(orderToMatch.cusip) as RawSecurity),
    agreements,
    fee: !orderToMatch.fee ? null : new Decimal(orderToMatch.fee),
    rebate: !orderToMatch.rebate ? null : new Decimal(orderToMatch.rebate),
    orderType: orderToMatch.orderType === OmsOrderType.MARKET ? 'MARKET' : 'LIMIT',
    orderIntent: 'FIRM',
    timeInForceType: 'DAY',
    goodTillTime: null,
    isAnonymous: false,
    allowOddLots: true,
  };

  populateForm(payload);
}

async function submitForm(active: boolean): Promise<void> {
  if (formStatus.value !== 'idle' || !(await validate())) return;

  apiErrors.value = [];
  formStatus.value = 'submitting';

  try {
    if (typeof myOrderSelected.value === 'object') {
      await splitOrder(myOrderSelected.value);
    } else if (order.value) {
      await editOrder(order.value as unknown as FrontendOrder, active);
    } else {
      await createOrder(active);
    }

    resetFields();
    order.value = null;
    emit('success');
    emit('input', false);
    formStatus.value = 'idle';
  } catch (err) {
    apiErrors.value = [new ApiError(i18nServerMessage(err as Error)).message];
    formStatus.value = 'idle';
  }
}

async function splitOrder(myOrderSelected: Data<FrontendOrder>): Promise<void> {
  const result = await marketplaceService.splitOrder({
    id: myOrderSelected.orderRef,
    agreementIds: formFields.value.agreements.map((c) => c.id),
    quantity: BigInt(formFields.value.quantity),
    rateLimit: {
      type: RateType.REBATE,
      value: formFields.value.rebate?.toString(),
    },
  });
  if (!result.success) {
    snackbarManager.show({
      message: i18n.t(result.error, result.details) as string,
      color: 'red',
    });
    return;
  }

  emit('action', {
    orderRef: result.data.id,
    message: 'Order successfully split',
  });

  void cmpMyOrderPicker.value?.refreshMyOrders();
}

async function editOrder(order: FrontendOrder, active: boolean): Promise<void> {
  await marketplaceApiService.editOrder(
    ModifyOmsOrderRequest.fromModel({
      orderRef: order.orderRef,
      rateType: rateTypeStore.marketplaceRateType === RateType.REBATE ? 'REBATE' : 'FEE',
      rate: rate.value,
      ...pickCommonFormFields(active),
    })
  );

  emit('action', {
    orderRef: order.orderRef,
    message: 'Order successfully edited',
    // only show button if OrderDetailsDialog is closed
  });
}

async function createOrder(active: boolean): Promise<void> {
  const { orderRef } = await marketplaceApiService.createOrder(
    CreateOmsOrderRequest.fromModel({
      rateType: rateTypeStore.marketplaceRateType === RateType.REBATE ? 'REBATE' : 'FEE',
      rate: rate.value,
      side: formFields.value.side,
      security: formFields.value.security,
      ...pickCommonFormFields(active),
    })
  );

  emit('action', {
    orderRef: orderRef,
    message: 'Order successfully created',
  });
}

function resetFields(): void {
  security.value = null;
  quantity.value = null;
  fee.value = null;
  rebate.value = null;
  minQuantity.value = defaultMinQuantity.value;
  filled.value = 0;
  agreements.value = [];
  orderType.value = 'LIMIT';
  orderIntent.value = 'FIRM';
  timeInForceType.value = 'DAY';
  goodTillTime.value = [nextHour.value.getHours(), 0];
  isAnonymous.value = false;
  allowOddLots.value = true;
  apiErrors.value = [];

  agreementSelectorKey.value++;
  expandedPanel.value = null;
  localShowSummary.value = false;
  minQuantityTouched.value = false;

  apiErrors.value = [];
}

function handleResetButton(): void {
  if (isEditMode.value && order.value) {
    // restore original order values
    initializeFormFromOrder();
  } else {
    // reset to defaults (create mode)
    resetFields();
  }
  resetValidation();
}

function pickCommonFormFields(
  active: boolean
): Omit<
  Api.Marketplace.OrderCommonFormRequest,
  'openQuantity' | 'totalValue' | 'rebate' | 'firmQuantity' | 'ioiQuantity'
> {
  return {
    active,
    fee: formFields.value.fee,
    quantity: formFields.value.quantity,
    minQuantity: formFields.value.minQuantity,
    agreementIds: formFields.value.agreements.map((c) => c.id),
    orderType: formFields.value.orderType,
    orderIntent: formFields.value.orderIntent,
    timeInForceType: formFields.value.timeInForceType,
    goodTillTime: formFields.value.goodTillTime,
    isAnonymous: formFields.value.isAnonymous,
    allowOddLots: formFields.value.allowOddLots,
  };
}

async function goToSummary(): Promise<void> {
  if (!(await validate())) {
    if (rules.minQuantity.errorMessages.length) {
      // minQuantity is hidden because "advanced fields" is not expanded by default
      // the other "advanced fields" won't become invalid because
      // they are either required or have a default value
      expandedPanel.value = 0;
    }
    return;
  }

  // get price from passed order prop if edit mode
  // or from selected security if create mode
  const lastClosePrice = order.value
    ? order.value.security.lastClosePrice
    : security.value
      ? security.value.lastClosePrice
      : null;

  const notional = calculateOrderNotional(quantity.value, lastClosePrice);

  // bit quirky but we need to set navDrawerPermanent=true to avoid the click in the risk limit dialog closing it
  // and then we'll make it non-permanent again as soon as the risk limit check is done
  navDrawerPermanent.value = true;

  // will stop workflow and display soft/hard limits dialog if needed
  // it either immediately fires the accept or reject, or first shows a dialog before firing either
  new RiskLimitValidator(dialogManager, loginState.value.user!).checkAndConfirmRiskLimits(
    notional,
    () => {
      localShowSummary.value = true;
      navDrawerPermanent.value = false;
    },
    () => {
      navDrawerPermanent.value = false;
    }
  );
}

function isPresent(value: unknown) {
  return typeof value === 'string' ? !!value.trim() : !!value;
}

function isInteger(value: unknown) {
  return typeof value === 'number' && value % 1 === 0;
}

function formatRate(rate: number): string {
  return formatDecimalAsString(new Decimal(rate), RATE_PRECISION);
}

const { rules, validate, resetValidation, isSubmitted } = useRules<
  {
    security: Security | null;
    quantity: string;
    rate: string;
    minQuantity: string;
    goodTillTime: TimeTuple;
    agreements: AgreementInfo | null;
    changed: unknown;
  },
  {
    quantity: number | null;
    rate: Decimal | null;
    minQuantity: number | null;
  }
>({
  security: {
    rules: [
      (v) => isPresent(v) || 'Please select a security.',
      (v) => v === null || v.cannotTradeMessage || true,
    ],
  },
  quantity: {
    transform: (v) => (!v ? null : Number(v.replaceAll(/[^\d.-]/g, ''))),
    rules: [
      (v) => v !== null || 'Please enter a quantity.',
      (v) => isInteger(v) || 'Please enter a valid quantity.',
      (v) => v === null || v > 0 || 'Must be greater than 0.',
      (v) => v === null || v <= 999999999 || 'Must equal or be less than 999,999,999.',
      (v) =>
        v === null ||
        filled.value === 0 ||
        v > filled.value ||
        'Must be greater than the executed quantity.',
      (v) =>
        v === null ||
        !isSplittingOrder.value ||
        !props.orderToMatch ||
        v <= Number(props.orderToMatch.quantity) ||
        `Must be less than or equal to selected order quantity (${formatPrettyNumber(props.orderToMatch.quantity)}).`,
      (v) => {
        if (allowOddLots.value) return true;
        // defaultMinQuantity is the odd lot size — quantity must be a multiple of it
        return (
          v === null ||
          v % defaultMinQuantity.value === 0 ||
          `This order cannot be created with an odd lot quantity.`
        );
      },
    ],
  },
  rate: {
    transform: (v) => (!v || v === '-' ? null : new Decimal(v.replaceAll(/[^\d.-]/g, ''))),
    rules: [
      (v) => isPresent(v) || orderIntent.value === 'IOI' || 'Rate is required.',
      (v) =>
        v === null ||
        notGreaterThanPrecision(RATE_PRECISION)(v) ||
        `Rate must not be more than ${RATE_PRECISION} decimal places.`,
      (v) =>
        v === null || v.gte(-MAX_RATE) || `Rate cannot be lower than -${formatRate(MAX_RATE)}%.`,
      (v) => v === null || v.lte(MAX_RATE) || `Rate cannot be more than ${formatRate(MAX_RATE)}%.`,
    ],
  },
  minQuantity: {
    transform: (v) => (!v ? null : Number(v.replaceAll(/[^\d.-]/g, ''))),
    rules: [
      () => ({ trigger: ['quantity'] }),
      (v) => v === null || isInteger(v) || 'Please enter a valid min. quantity.',
      (v) => v === null || v >= 0 || 'Must be greater than or equal to 0.',
      (v) =>
        v === null ||
        quantity.value === null ||
        v <= remainingQuantity.value ||
        `Must be less than or equal to remaining quantity (${formatPrettyNumber(
          remainingQuantity.value
        )}).`,
      (v) => {
        if (allowOddLots.value) return true;
        const lotSize = defaultMinQuantity.value;
        const effectiveMinQty = v ?? lotSize;
        return (
          (effectiveMinQty >= lotSize && effectiveMinQty % lotSize === 0) ||
          `Min. Quantity (${effectiveMinQty}) is not aligned with lot size (${lotSize}). Enable "Allow Odd Lots" or adjust Min. Quantity.`
        );
      },
    ],
  },
  goodTillTime: {
    rules: [
      () =>
        timeInForceType.value !== 'GOOD_TILL_TIME' ||
        goodTillTimeDate.value === null ||
        goodTillTimeDate.value > currentTimeUTC.value ||
        'Must be in the future.',
      () =>
        timeInForceType.value !== 'GOOD_TILL_TIME' ||
        goodTillTimeDate.value === null ||
        isTimeInTimeframe(
          // Note: we do -1 min for the timeframe check to easily let the user specify exactly the timeframe cut-off
          subMinutes(goodTillTimeDate.value, 1),
          clientConfig.value.marketTimeframes.createLoans
        ) ||
        'Must be within market hours.',
    ],
  },
  agreements: {
    rules: [
      () => Promise.resolve(agreementIdsEligible.value.size > 0 || 'No agreements eligible.'),
      () =>
        Promise.resolve(
          agreements.value.length > 0 || 'Please select an agreement for this order.'
        ),
      () =>
        Promise.resolve(
          agreements.value.every((agreement) => agreementIdsEligible.value.has(agreement.id)) ||
            'Some of your selected agreements are not eligible.'
        ),
    ],
  },
  changed: {
    rules: [
      () =>
        !isSubmitted.value ||
        order.value === null ||
        side.value !== order.value.side ||
        security.value !== order.value.security ||
        quantity.value !== order.value.quantity ||
        rebate.value?.toNumber() !== order.value.rebate?.toNumber() ||
        minQuantity.value !== order.value.minQuantity ||
        timeInForceType.value !== order.value.timeInForceType ||
        (timeInForceType.value === 'GOOD_TILL_TIME' &&
          goodTillTimeDate.value !== order.value.goodTillTime) ||
        agreements.value
          .map((a) => a.id)
          .sort()
          .toString() !==
          order.value.agreements
            .map((a) => a.id)
            .sort()
            .toString() ||
        'Order is already in the requested state.',
    ],
  },
});

watch(remainingQuantity, () => validate('minQuantity'));
watch(allowOddLots, () => {
  void validate('quantity');
  void validate('minQuantity');
});
watchEffect(() => validate('agreements'));
watchEffect(() => validate('goodTillTime'));
watchEffect(() => validate('changed'));

const cmpMyOrderPicker = shallowRef<typeof MarketplaceOrderFormMyOrderPicker | null>(null);
// will be defined once the picker loads options
const myOrderSelected = shallowRef<Data<FrontendOrder> | OrderSelection>(
  OrderSelection.NO_PREFERENCE
);

watch(myOrderSelected, async (order) => {
  if (typeof order !== 'object' || !props.shouldSplit || !props.orderToMatch) return;
  await nextTick();
  quantity.value = Math.min(Number(order.openQuantity), Number(props.orderToMatch.quantity));
});

const isSplittingOrder = computed(
  () => props.shouldSplit && typeof myOrderSelected.value === 'object'
);

watch(
  () => props.shouldSplit,
  (shouldSplit) => {
    if (!shouldSplit) {
      myOrderSelected.value = OrderSelection.NO_PREFERENCE;
    }
  }
);

// executed when the user clicks the MATCH button
watch(myOrderSelected, (order) => {
  if (typeof order !== 'object' || !props.shouldSplit) return;

  populateForm({
    agreements: agreements.value,
    fee: order.fee === undefined ? null : new Decimal(order.fee),
    filled: 0,
    goodTillTime: ifTimestampToDate((order.goodTillTime as Timestamp | undefined) ?? null),
    isAnonymous: false,
    allowOddLots: true,
    minQuantity: Number(order.minQuantity),
    orderType: order.orderType === OmsOrderType.MARKET ? 'MARKET' : 'LIMIT',
    orderIntent: 'FIRM',
    quantity: Math.min(Number(props.orderToMatch!.quantity), Number(order.quantity)),
    rebate:
      props.orderToMatch?.rebate === undefined ? null : new Decimal(props.orderToMatch.rebate),
    security: Security.fromData(securitiesStore.getSecurity(order.cusip) as RawSecurity),
    side: order.side === Side.BORROW ? 'BORROW' : 'LOAN',
    timeInForceType: OmsTimeInForceType[order.timeInForceType] as TimeInForceType,
  });
});

defineExpose({
  test: {
    populateForm,
    validate,
    gttTupleToDate,
    setMinQuantity: (value: number) => (minQuantity.value = value),
    setAllowOddLots: (value: boolean) => (allowOddLots.value = value),
  },
});

const showForm = shallowRef(false);
watchEffect(() => props.value && (showForm.value = true));

function goBack() {
  if (localShowSummary.value) {
    localShowSummary.value = false;
  } else {
    emit('input', false);
  }
}

// VNavigationDrawer, nor VForm, don't support keyup.esc,
// and VCard doesn't propagate it back from the "show summary" step
useEventListener('keyup', (event) => {
  if (!showForm.value) return;
  if (event.code === 'Escape') goBack();
});
</script>

<template>
  <VNavigationDrawer
    fixed
    :permanent="navDrawerPermanent"
    right
    style="height: calc(100vh - 48px); margin-top: 48px"
    temporary
    :value="value"
    width="600px"
    @input="emit('input', $event)"
    @transitionend="showForm = value"
  >
    <VForm
      v-if="showForm"
      id="marketplace-order-form"
      :data-test="value ? 'marketplace-order-form' : undefined"
      novalidate
      style="height: 100%"
      @submit.prevent
    >
      <VCard class="responsive-card">
        <VCardTitle>
          <span class="headline"> {{ formTitle }} Order </span>
          <VSpacer />
          <VBtn class="close-icon" icon @click="emit('input', false)">
            <VIcon>mdi-close</VIcon>
          </VBtn>
        </VCardTitle>

        <VCardText class="scrollable-card-text">
          <VContainer v-if="!localShowSummary" class="grid">
            <VRadioGroup
              v-model="orderIntent"
              data-test="order-intent"
              :disabled="isEditMode || isSplittingOrder"
              label="Expose to venue as"
              row
            >
              <VRadio
                v-for="option in orderIntentOptions"
                :key="option.value"
                :data-test-id="`order-intent-${option.value}`"
                :label="option.text"
                :value="option.value"
              ></VRadio>
            </VRadioGroup>
            <MarketplaceOrderFormMyOrderPicker
              v-if="clientConfig.orderbookSplitOrdersEnabled && shouldSplit"
              :ref="(cmp) => (cmpMyOrderPicker = cmp)"
              v-model="myOrderSelected"
              :order-to-match="orderToMatch"
            />
            <VCheckbox v-model="allowOddLots" class="mt-4" :disabled="isEditMode">
              <template #label>
                <span>Allow Odd Lots</span>
                <VTooltip bottom open-delay="300">
                  <template #activator="{ on, attrs }">
                    <VIcon v-bind="attrs" class="grey--text ml-1" small v-on="on">
                      mdi-information-outline
                    </VIcon>
                  </template>
                  <div>
                    When unchecked, quantity and min. quantity must be multiples of the lot size.
                    <ul class="pl-4 mt-1">
                      <li>Equities: 100</li>
                      <li>Bonds: 1,000</li>
                    </ul>
                  </div>
                </VTooltip>
              </template>
            </VCheckbox>
            <VSelect
              v-model="side"
              :disabled="isEditMode || isSplittingOrder"
              :items="sideSelectorItems"
              label="Side"
            />
            <SimpleSecuritySearch
              v-model="security"
              :autofocus="security === null"
              data-test="security"
              :disabled="isEditMode || !!orderToMatch || isSplittingOrder"
              v-bind="rules.security"
              label="Security"
            />
            <NumericInput
              v-model="quantity"
              :autofocus="security !== null"
              data-test="quantity"
              v-bind="rules.quantity"
              :label="`Quantity${order && filled > 0 ? ` (executed: ${filled})` : ''}`"
              :min="0"
              :step="100"
              type="integer"
            />
            <div class="grid grid-cols-2 gap-0.5">
              <VSelect
                v-model="rateTypeStore.marketplaceRateType"
                data-test="rate-type"
                :items="rateTypeOptions"
                label="Rate Type"
                tabindex="-1"
              ></VSelect>
              <NumericInput
                v-model="rate"
                data-test="rate"
                :disabled="isMarketOrder"
                v-bind="rules.rate"
                label="Rate"
                :precision="RATE_PRECISION"
                :step="0.25"
                suffix="%"
                type="decimal"
              />
            </div>
            <NumericInput
              v-bind="rules.minQuantity"
              v-model="minQuantity"
              data-test="minQuantity"
              :disabled="!clientConfig.orderbookMinQuantityEditable || isSplittingOrder"
              label="Min. Quantity"
              :max="quantity === null ? 1 : quantity - filled"
              :min="1"
              :step="100"
              type="integer"
            />
            <VTextField
              v-if="isSplittingOrder"
              data-test="time-in-force"
              disabled
              label="Time in Force"
              value="Fill or kill"
            />
            <VSelect
              v-else
              v-model="timeInForceType"
              data-test="time-in-force"
              :disabled="isSplittingOrder"
              item-text="text"
              item-value="value"
              :items="timeInForceOptions"
              label="Time in Force"
            />
            <PlainTimePicker
              v-if="timeInForceType == 'GOOD_TILL_TIME'"
              v-model="goodTillTime"
              v-bind="rules.goodTillTime"
              :disabled="isSplittingOrder"
              :label="'Good Till Time (' + localTimezone + ')'"
            />

            <!--
              The "v-if" below prevents race conditions when fetching order data.
              Without this guard, the component can render with stale props before
              the order fetch completes, potentially causing duplicate API calls
              where slower responses override faster ones (ListAgreements endpoint).
             -->
            <!-- Key forces remount on reset so default agreements are re-applied -->
            <MultipleTableAgreementSelector
              v-if="!isEditMode || order || props.orderToMatch"
              :key="agreementSelectorKey"
              v-model="agreements"
              data-test="agreements"
              v-bind="rules.agreements"
              :security="security"
              show-counterparty-breakdown
              :side="Side[side]"
              :use-default-agreements="!isEditMode && !props.orderToMatch"
              @agreement-ids-eligible="agreementIdsEligible = $event"
            />

            <div v-if="apiErrors.length || rules.changed.errorMessages.length" class="px-1 mt-4">
              <div class="v-alert v-alert--dense text--primary text-body-2 text-center error">
                {{ apiErrors.join('\n') }}
                {{ rules.changed.errorMessages.join('\n') }}
              </div>
            </div>
          </VContainer>

          <VContainer
            v-if="localShowSummary"
            v-shortkey="['enter']"
            @shortkey="submitForm(isEditMode ? order?.status !== 'HELD' : true)"
          >
            <div class="py-0 text--primary">
              <h2 class="text-h6 text--secondary">Continue?</h2>
              <MarketplaceOrderSummary
                hide-routing
                :order="{
                  ...formFields,
                  ...(order && { createdAt: order.createdAt, orderRef: order.orderRef }),
                }"
                :total-eligible-agreements="agreementIdsEligible.size"
              />
            </div>

            <VDivider />

            <div v-if="apiErrors.length" class="pa-1 px-1 mt-4 mb-n4">
              <div class="v-alert v-alert--dense text--primary text-body-2 text-center error">
                {{ apiErrors.join('\n') }}
              </div>
            </div>
          </VContainer>
        </VCardText>

        <VCardActions class="d-flex">
          <div class="d-flex flex-grow-1 justify-space-between align-end px-5 py-2 button-row">
            <template v-if="!localShowSummary">
              <VBtn color="secondary" @click="handleResetButton"> Reset </VBtn>
              <VBtn color="primary" type="submit" @click="goToSummary()">
                {{ isEditMode ? 'Edit' : 'Create' }}</VBtn
              >
            </template>

            <template v-if="localShowSummary">
              <VBtn
                color="secondary"
                :disabled="formStatus !== 'idle'"
                @click="
                  resetValidation();
                  localShowSummary = false;
                "
              >
                Back
              </VBtn>

              <AuroraBtnDropdown
                v-if="order"
                class="split-button"
                color="primary"
                data-test="save-button"
                :disabled="formStatus !== 'idle'"
                :loading="formStatus === 'submitting'"
                :main-text="order.status === 'HELD' ? 'Save inactive' : 'Save active'"
                split
                timeframe="createLoans"
                @click="submitForm(order ? order.status !== 'HELD' : true)"
              >
                <VList ref="route-actions" dense>
                  <VListItem data-test="save-routed-menu-item" @click="submitForm(true)">
                    <VListItemTitle>Save active</VListItemTitle>
                  </VListItem>
                  <VListItem data-test="save-unrouted-menu-item" @click="submitForm(false)">
                    <VListItemTitle>Save inactive</VListItemTitle>
                  </VListItem>
                </VList>
              </AuroraBtnDropdown>
              <AuroraBtnDropdown
                v-else
                class="split-button"
                color="primary"
                data-test="create-button"
                :disabled="formStatus !== 'idle'"
                :loading="formStatus === 'submitting'"
                main-text="Create active"
                split
                timeframe="createLoans"
                @click="submitForm(true)"
              >
                <VList ref="route-actions" dense>
                  <VListItem data-test="create-routed-menu-item" @click="submitForm(true)">
                    <VListItemTitle>Create active</VListItemTitle>
                  </VListItem>
                  <VListItem data-test="create-unrouted-menu-item" @click="submitForm(false)">
                    <VListItemTitle>Create inactive</VListItemTitle>
                  </VListItem>
                </VList>
              </AuroraBtnDropdown>
            </template>
          </div>
        </VCardActions>
      </VCard>
    </VForm>
  </VNavigationDrawer>
</template>

<style lang="scss">
body:has(#marketplace-order-form) .v-overlay {
  transition-property: opacity; // we don't want the margin below to be animated
  margin-top: 48px; // we don't want overlay to cover header
}
</style>

<style lang="scss" scoped>
.button-row {
  display: flex;
  gap: 1rem;
  & > * {
    flex-basis: 0;
    flex-grow: 1;
  }
}

.split-button {
  display: flex;
  & > :first-child {
    flex-shrink: 1;
  }
}

.grid {
  display: grid;
}

.gap-0\.5 {
  gap: 0.5rem;
}

.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.text-center {
  text-align: center;
}

.responsive-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
}

.scrollable-card-text {
  flex: 1 1 auto;
  overflow-y: auto;
}

/* when the form is used within the orderbook (and not the dialog) */
.orderbook .headline {
  margin-left: 10px;
}

::v-deep {
  .v-expansion-panel-content__wrap {
    padding: 0;
  }

  .grid:has(.v-label) {
    row-gap: 1rem;
    align-items: end;

    // Active labels occupy more space on top, so adjusting the margin accordingly
    & > :has(.v-label) {
      interpolate-size: allow-keywords;
      transition: margin ease-in-out 300ms;

      &:not(:has(.v-label--active)) {
        margin-top: -18px !important;
      }
    }

    // Error messages occupy more space on bottom, so adjusting their height accordingly
    .v-text-field__details,
    .v-messages {
      interpolate-size: allow-keywords;
      transition:
        height ease-in-out 300ms,
        min-height ease-in-out 300ms;
      overflow: clip;

      &:has(.v-messages__wrapper:empty) {
        height: 0;
        min-height: 0;
      }
    }
  }
}
</style>
